import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise, lineAmount } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';

/**
 * The project workspace: header figures and the six transaction ledgers.
 *
 * Every list here filters on approval_status = 'approved'. Pending site entries
 * exist in the same tables but must not move any number until an admin approves
 * them — that is the product's central rule, and the reason the filter is
 * repeated rather than abstracted away where it could be forgotten.
 */

function unwrap<T>({ data, error }: { data: T; error: { message: string } | null }): NonNullable<T> {
  if (error) throw new Error(error.message);
  if (data === null || data === undefined) throw new Error('No data returned');
  return data as NonNullable<T>;
}

export type LedgerTab =
  | 'payments' | 'purchases' | 'usage' | 'labour' | 'expenses' | 'transfers' | 'drawings' | 'elevations';

export const pKeys = {
  project: (id: string) => ['project', id] as const,
  ledger: (id: string, tab: LedgerTab) => ['project', id, tab] as const,
};

/* ------------------------------ header ------------------------------ */

import { fetchSupplierTransactions, fetchSubcontractorTransactions } from './transactions';

export interface ProjectHeader {
  id: string;
  name: string;
  location: string | null;
  status: string;
  startDate: string | null;
  description: string | null;
  valuePaise: number;
  paidPaise: number;
  pendingPaise: number;
  clientId: string;
  clientName: string;
  clientPhone: string;
  materialTotalPaise: number;
  materialPaidPaise: number;
  subcontractorTotalPaise: number;
  subcontractorPaidPaise: number;
  expenseTotalPaise: number;
  transferredInPaise: number;
  transferredOutPaise: number;
  netTransferPaise: number;
  totalExpensesPaise: number;
}

export function useProject(projectId: string) {
  return useQuery({
    queryKey: pKeys.project(projectId),
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectHeader> => {
      const [
        pRes,
        paymentsRes,
        purchasesRes,
        labourRes,
        expensesRes,
        transfersInRes,
        transfersOutRes,
        supplierTx,
        subTx,
      ] = await Promise.all([
        supabase
          .from('projects')
          .select('id, name, location, status, start_date, description, value_paise, client_id, clients(name, phone)')
          .eq('id', projectId)
          .is('deleted_at', null)
          .single(),
        supabase
          .from('client_payments')
          .select('amount_paise')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('material_purchases')
          .select('amount_paise')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('subcontractor_labour')
          .select('total_amount_paise')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('expenses')
          .select('amount_paise')
          .eq('project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('stock_transfers')
          .select('amount_paise, rate_paise, quantity')
          .eq('to_project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        supabase
          .from('stock_transfers')
          .select('amount_paise, rate_paise, quantity')
          .eq('from_project_id', projectId)
          .eq('approval_status', 'approved')
          .is('deleted_at', null),
        fetchSupplierTransactions('', projectId),
        fetchSubcontractorTransactions({ projectId }),
      ]);

      const p = unwrap(pRes);
      const payments = unwrap(paymentsRes);
      const purchases = unwrap(purchasesRes);
      const labour = unwrap(labourRes);
      const expenses = unwrap(expensesRes);
      const transfersIn = transfersInRes.data ?? [];
      const transfersOut = transfersOutRes.data ?? [];

      const paidPaise = payments.reduce((s, r) => s + Number(r.amount_paise || 0), 0);
      const materialTotalPaise = purchases.reduce((s, r) => s + Number(r.amount_paise || 0), 0);
      const subcontractorTotalPaise = labour.reduce((s, r) => s + Number(r.total_amount_paise || 0), 0);
      const expenseTotalPaise = expenses.reduce((s, r) => s + Number(r.amount_paise || 0), 0);

      // Amount paid values (Actual outlay paid, not credit):
      const materialPaidPaise = supplierTx.reduce((s, r) => s + (r.paidAmountPaise || 0), 0);
      const subcontractorPaidPaise = subTx.reduce((s, r) => s + (r.paidAmountPaise || 0), 0);

      // Site Transfers:
      const transferredInPaise = transfersIn.reduce((s, r) => {
        const val = r.amount_paise != null ? Number(r.amount_paise) : (Number(r.quantity || 0) * Number(r.rate_paise || 0));
        return s + (val || 0);
      }, 0);
      const transferredOutPaise = transfersOut.reduce((s, r) => {
        const val = r.amount_paise != null ? Number(r.amount_paise) : (Number(r.quantity || 0) * Number(r.rate_paise || 0));
        return s + (val || 0);
      }, 0);
      const netTransferPaise = transferredInPaise - transferredOutPaise;

      // Total Expenses Paid: Only actual amount paid value for this project + net site transfers
      const totalExpensesPaise = materialPaidPaise + subcontractorPaidPaise + expenseTotalPaise + netTransferPaise;

      const client = p.clients as unknown as { name: string; phone: string } | null;

      return {
        id: p.id,
        name: p.name,
        location: p.location,
        status: p.status,
        startDate: p.start_date,
        description: p.description,
        valuePaise: p.value_paise,
        paidPaise,
        pendingPaise: Math.max(0, p.value_paise - paidPaise),
        clientId: p.client_id,
        clientName: client?.name ?? '—',
        clientPhone: client?.phone ?? '',
        materialTotalPaise,
        materialPaidPaise,
        subcontractorTotalPaise,
        subcontractorPaidPaise,
        expenseTotalPaise,
        transferredInPaise,
        transferredOutPaise,
        netTransferPaise,
        totalExpensesPaise,
      };
    },
  });
}

/* ------------------------------ ledgers ------------------------------ */

const APPROVED = 'approved';

export function useLedger(projectId: string, tab: LedgerTab) {
  return useQuery({
    queryKey: pKeys.ledger(projectId, tab),
    enabled: !!projectId,
    queryFn: async () => {
      switch (tab) {
        case 'payments':
          return unwrap(await supabase
            .from('client_payments')
            // No mode: client_payments does not record one — see 0015.
            .select('id, paid_on, payment_type, amount_paise, reference_no, remarks')
            .eq('project_id', projectId).eq('approval_status', APPROVED)
            .is('deleted_at', null).order('paid_on', { ascending: false }));

        case 'purchases':
          return unwrap(await supabase
            .from('material_purchases')
            .select('id, purchased_on, quantity, rate_paise, amount_paise, advance_paid_paise, invoice_no, remarks, material_id, supplier_id, unit_id, materials(name), suppliers(name), units(short_name)')
            .eq('project_id', projectId).eq('approval_status', APPROVED)
            .is('deleted_at', null).order('purchased_on', { ascending: false }));

        case 'usage': {
          const [usageRes, purchaseRes, transferRes] = await Promise.all([
            supabase
              .from('material_usage')
              .select('id, used_on, quantity, activity, remarks, material_id, unit_id, created_at, materials(name), units(short_name)')
              .eq('project_id', projectId)
              .eq('approval_status', APPROVED)
              .is('deleted_at', null)
              .order('used_on', { ascending: false }),
            supabase
              .from('material_purchases')
              .select('id, purchased_on, quantity, material_id, created_at')
              .eq('project_id', projectId)
              .eq('approval_status', APPROVED)
              .is('deleted_at', null),
            supabase
              .from('stock_transfers')
              .select('id, transferred_on, quantity, material_id, from_project_id, to_project_id, created_at')
              .or(`from_project_id.eq.${projectId},to_project_id.eq.${projectId}`)
              .eq('approval_status', APPROVED)
              .is('deleted_at', null),
          ]);

          const usageRows = unwrap(usageRes);
          const purchaseRows = unwrap(purchaseRes);
          const transferRows = unwrap(transferRes);

          // Calculate current live stock remaining per material for this project:
          // (Total approved purchases + Total approved in-transfers) - (Total approved usage + Total approved out-transfers)
          const currentStockByMaterial = new Map<string, number>();

          for (const p of purchaseRows) {
            if (!p.material_id) continue;
            currentStockByMaterial.set(
              p.material_id,
              (currentStockByMaterial.get(p.material_id) ?? 0) + Number(p.quantity),
            );
          }

          for (const t of transferRows) {
            if (!t.material_id) continue;
            const isIncoming = t.to_project_id === projectId;
            const delta = isIncoming ? Number(t.quantity) : -Number(t.quantity);
            currentStockByMaterial.set(
              t.material_id,
              (currentStockByMaterial.get(t.material_id) ?? 0) + delta,
            );
          }

          for (const u of usageRows) {
            if (!u.material_id) continue;
            currentStockByMaterial.set(
              u.material_id,
              (currentStockByMaterial.get(u.material_id) ?? 0) - Number(u.quantity),
            );
          }

          return usageRows.map((r) => ({
            ...r,
            remaining_quantity: Math.max(0, currentStockByMaterial.get(r.material_id) ?? 0),
          }));
        }

        case 'labour':
          return unwrap(await supabase
            .from('subcontractor_labour')
            .select('id, worked_on, worker_count, rate_per_worker_paise, total_amount_paise, remarks, subcontractor_id, work_type_id, subcontractors(name), work_types(name)')
            .eq('project_id', projectId).eq('approval_status', APPROVED)
            .is('deleted_at', null).order('worked_on', { ascending: false }));

        case 'expenses':
          return unwrap(await supabase
            .from('expenses')
            .select('id, spent_on, title, amount_paise, paid_to, bill_no, remarks, category_id, expense_categories(name)')
            .eq('project_id', projectId).eq('approval_status', APPROVED)
            .is('deleted_at', null).order('spent_on', { ascending: false }));

        case 'transfers':
          // Both directions: material sent from here and received here.
          return unwrap(await supabase
            .from('stock_transfers')
            .select('id, transferred_on, quantity, rate_paise, amount_paise, from_project_id, to_project_id, vehicle_no, driver_name, remarks, material_id, unit_id, materials(name), units(short_name), from_project:projects!stock_transfers_from_project_id_fkey(name), to_project:projects!stock_transfers_to_project_id_fkey(name)')
            .or(`from_project_id.eq.${projectId},to_project_id.eq.${projectId}`)
            .eq('approval_status', APPROVED)
            .is('deleted_at', null).order('transferred_on', { ascending: false }));

        case 'drawings':
          return unwrap(await supabase
            .from('project_drawings')
            .select('*')
            .eq('project_id', projectId)
            .is('deleted_at', null)
            .order('version', { ascending: false }));
      }
    },
  });
}

/* ------------------------------ mutations ------------------------------ */

/** Refresh the project header plus every ledger, stock calculation, and report after any entry is saved. */
function useInvalidateProject(projectId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['project', projectId] });
    void qc.invalidateQueries({ queryKey: ['project'] });
    void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['client'] });
    void qc.invalidateQueries({ queryKey: ['materials'] });
    void qc.invalidateQueries({ queryKey: ['material'] });
    void qc.invalidateQueries({ queryKey: ['material-full-report'] });
    void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
    void qc.invalidateQueries({ queryKey: ['stock-transfers'] });
    void qc.invalidateQueries({ queryKey: ['bills'] });
    void qc.invalidateQueries({ queryKey: ['suppliers'] });
    void qc.invalidateQueries({ queryKey: ['supplier'] });
    void qc.invalidateQueries({ queryKey: ['subcontractors'] });
    void qc.invalidateQueries({ queryKey: ['subcontractor'] });
    void qc.invalidateQueries({ queryKey: ['report'] });
    void qc.invalidateQueries({ queryKey: ['approvals'] });
    void qc.invalidateQueries({ queryKey: ['my-submissions'] });
    void qc.invalidateQueries({ queryKey: ['staff-dashboard'] });
  };
}

export interface EntryBase { date: string; remarks?: string }

export function useAddPayment(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async (v: EntryBase & {
      payment_type: string; amountRupees: string; reference_no?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(await supabase.from('client_payments').insert({
        company_id: actor.companyId, project_id: projectId,
        paid_on: v.date,
        payment_type: v.payment_type as never,
        amount_paise: toPaise(v.amountRupees),
        // mode is deliberately absent, not sent: the form no longer asks for
        // it, and the column's own default ('neft') applies on insert.
        reference_no: v.reference_no?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: invalidate,
  });
}

export function useAddPurchase(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async (v: EntryBase & {
      material_id: string; supplier_id: string; unit_id?: string;
      quantity: string; rateRupees: string; advanceRupees?: string; invoice_no?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      const qty = Number(v.quantity);
      const rate = toPaise(v.rateRupees);
      // quantity x rate rounded to whole paise, so 2.4 t at ₹66,500 is exactly
      // ₹1,59,600 rather than a float artefact.
      const amount = lineAmount(qty, rate);
      const advance = v.advanceRupees?.trim() ? toPaise(v.advanceRupees) : 0;
      if (advance > amount) throw new Error('Advance cannot be more than the total amount');

      return unwrap(await supabase.from('material_purchases').insert({
        company_id: actor.companyId, project_id: projectId,
        material_id: v.material_id, supplier_id: v.supplier_id,
        unit_id: v.unit_id || null,
        purchased_on: v.date,
        quantity: qty, rate_paise: rate, amount_paise: amount,
        advance_paid_paise: advance,
        invoice_no: v.invoice_no?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: invalidate,
  });
}

export function useAddUsage(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async (v: EntryBase & {
      material_id: string; unit_id?: string; quantity: string; activity?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(await supabase.from('material_usage').insert({
        company_id: actor.companyId, project_id: projectId,
        material_id: v.material_id, unit_id: v.unit_id || null,
        used_on: v.date, quantity: Number(v.quantity),
        activity: v.activity?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: invalidate,
  });
}

export function useAddLabour(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async (v: EntryBase & {
      subcontractor_id: string; work_type_id: string;
      worker_count: string; ratePerWorkerRupees?: string; totalRupees?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      const count = Number(v.worker_count);
      const rate = v.ratePerWorkerRupees?.trim() ? toPaise(v.ratePerWorkerRupees) : null;
      // Total is derived from headcount x rate unless typed in explicitly.
      const total = v.totalRupees?.trim()
        ? toPaise(v.totalRupees)
        : rate !== null ? lineAmount(count, rate) : 0;

      return unwrap(await supabase.from('subcontractor_labour').insert({
        company_id: actor.companyId, project_id: projectId,
        subcontractor_id: v.subcontractor_id, work_type_id: v.work_type_id,
        worked_on: v.date, worker_count: count,
        rate_per_worker_paise: rate, total_amount_paise: total,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: invalidate,
  });
}

export function useAddExpense(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  return useMutation({
    mutationFn: async (v: EntryBase & {
      title: string; category_id?: string; amountRupees: string; paid_to?: string; bill_no?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      return unwrap(await supabase.from('expenses').insert({
        company_id: actor.companyId, project_id: projectId,
        category_id: v.category_id || null,
        spent_on: v.date, title: v.title.trim(),
        amount_paise: toPaise(v.amountRupees),
        paid_to: v.paid_to?.trim() || null,
        bill_no: v.bill_no?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: invalidate,
  });
}

export function useAddTransfer(projectId: string) {
  const actor = useSession((s) => s.actor);
  const invalidate = useInvalidateProject(projectId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: EntryBase & {
      to_project_id: string; material_id: string; unit_id?: string;
      quantity: string; rateRupees?: string;
      vehicle_no?: string; driver_name?: string;
    }) => {
      if (!actor) throw new Error('Not signed in');
      if (v.to_project_id === projectId) throw new Error('Choose a different site to transfer to');
      const qty = Number(v.quantity);
      const ratePaise = v.rateRupees && v.rateRupees.trim() ? toPaise(v.rateRupees) : null;
      const amountPaise = ratePaise != null ? Math.round(qty * ratePaise) : null;

      return unwrap(await supabase.from('stock_transfers').insert({
        company_id: actor.companyId,
        from_project_id: projectId, to_project_id: v.to_project_id,
        material_id: v.material_id, unit_id: v.unit_id || null,
        transferred_on: v.date, quantity: qty,
        rate_paise: ratePaise,
        amount_paise: amountPaise,
        vehicle_no: v.vehicle_no?.trim() || null,
        driver_name: v.driver_name?.trim() || null,
        remarks: v.remarks?.trim() || null,
      }).select('id').single());
    },
    onSuccess: (_data, v) => {
      invalidate();
      void qc.invalidateQueries({ queryKey: ['project', v.to_project_id] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map', v.to_project_id] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map', projectId] });
    },
  });
}
